import { CheckCircle2, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ReviewDetails } from './adapter/types';
import { LegalDisclaimer } from './LegalDisclaimer';
import { SignatureFieldMiniMap } from './SignatureFieldMiniMap';
import { type SignatureField } from './SignatureFieldOverlay';
import { isValidEffectiveDate, type Signatory, type SignatoryEvent } from './utils';

interface SidebarDetailsTabProps {
  /**
   * Document metadata to display. A plain value object rather than the document
   * itself, so this tab works for any document model — a fixed-DOCX contract has
   * no `LegalDocument` and simply omits the optional fields.
   */
  details: ReviewDetails;
  signatureFields: SignatureField[];
  numPages: number | null;
  signatories: Signatory[];
  signatoryEventByEmail: Map<string, SignatoryEvent>;
  currentPage: number;
  onPageClick: (page: number) => void;
  isFixedDocx?: boolean;
}

/**
 * "Details" tab of the review sidebar: signature-field summary + mini-map,
 * document metadata (title / effective date / jurisdiction), the signatory
 * list with signed/failed status badges, and the legal disclaimer.
 */
export function SidebarDetailsTab({
  details,
  signatureFields,
  numPages,
  signatories,
  signatoryEventByEmail,
  currentPage,
  onPageClick,
  isFixedDocx = false,
}: SidebarDetailsTabProps) {
  const { t } = useTranslation();

  return (
    <>
      {signatureFields.length > 0 && (
        <div className="rounded-xl border border-border bg-muted/60 p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-[hsl(var(--fg))] text-sm">
              {t('GENERATEREVIEW_SIGNATUREFIELDS_TITLE')}
            </h3>
            <span className="text-xs font-semibold text-globe-grey">
              {t('GENERATEREVIEW_SIGNATUREFIELDS_PLACEDCOUNT', { count: signatureFields.length })}
            </span>
          </div>
          <p className="text-sm text-globe-grey">{t('GENERATEREVIEW_SIGNATUREFIELDS_HELP')}</p>
        </div>
      )}

      {numPages && numPages > 0 && signatureFields.length > 0 && (
        <div className="border-t border-border pt-6">
          <SignatureFieldMiniMap
            numPages={numPages}
            signatureFields={signatureFields}
            signatories={signatories}
            currentPage={currentPage}
            onPageClick={onPageClick}
          />
        </div>
      )}

      <div className="border-t border-border pt-6">
        <h3 className="font-semibold text-[hsl(var(--fg))] text-sm mb-3">
          {t('GENERATEREVIEW_DETAILS_TITLE')}
        </h3>
        <dl className="space-y-3 text-sm">
          <div className="pb-3 border-b border-border">
            <dt className="text-globe-grey text-sm mb-1">{t('GENERATEREVIEW_DETAILS_DOCTITLE')}</dt>
            <dd className="font-medium text-[hsl(var(--fg))] text-sm">{details.title}</dd>
          </div>
          {isValidEffectiveDate(details.effectiveDate) && details.effectiveDate && (
            <div className="pb-3 border-b border-border">
              <dt className="text-globe-grey text-sm mb-1">
                {t('GENERATEREVIEW_DETAILS_EFFECTIVEDATE')}
              </dt>
              <dd className="font-medium text-[hsl(var(--fg))] text-sm">
                {new Date(details.effectiveDate).toLocaleDateString()}
              </dd>
            </div>
          )}
          {details.jurisdiction && (
            <div className="pb-3 border-b border-border">
              <dt className="text-globe-grey text-sm mb-1">
                {t('GENERATEREVIEW_DETAILS_JURISDICTION')}
              </dt>
              <dd className="font-medium text-[hsl(var(--fg))] text-sm">{details.jurisdiction}</dd>
            </div>
          )}
        </dl>
      </div>

      {signatories.length > 0 && (
        <div className="border-t border-border pt-6">
          <h3 className="font-semibold text-[hsl(var(--fg))] text-sm mb-3">
            {t('GENERATEREVIEW_DETAILS_SIGNATORIES')}
          </h3>
          <div className="space-y-3">
            {signatories.map((sig, index) => {
              const event = sig.email ? signatoryEventByEmail.get(sig.email) : undefined;
              const isSigned = event?.eventType === 'signatory_signed_success';
              const isFailed = event?.eventType === 'signatory_signed_failed';
              const signedAt = event?.signedTime ?? event?.occurredAt;
              return (
                <div key={index} className="p-3 rounded-lg border border-border bg-muted/60">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-[hsl(var(--fg))]">{sig.name}</p>
                      {sig.role && <p className="text-sm text-globe-grey">{sig.role}</p>}
                      {sig.email && <p className="text-sm text-globe-grey truncate">{sig.email}</p>}
                    </div>
                    <span
                      className="mt-1 inline-flex h-2.5 w-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: sig.color }}
                      aria-hidden
                    />
                  </div>

                  {(isSigned || isFailed) && (
                    <div
                      className={
                        'mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-semibold ' +
                        (isSigned
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : 'bg-orange-50 text-orange-700 border border-orange-200')
                      }
                    >
                      {isSigned ? (
                        <CheckCircle2 className="size-3.5" />
                      ) : (
                        <XCircle className="size-3.5" />
                      )}
                      <span>{isSigned ? 'Signed' : 'Sign failed'}</span>
                      {signedAt ? (
                        <span className="font-normal text-medium-emphasis">
                          ·{' '}
                          {new Date(signedAt).toLocaleString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      ) : null}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {
        !isFixedDocx && (<div className="border-t border-border pt-6">
          <LegalDisclaimer />
        </div>)
      }
    </>
  );
}
